import { FoldVertical } from 'lucide-react';
import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@shared/components/ui';

import { drawnShare, sharePercent, tokenCount } from '../data/context-figures';
import type { MeterFigure } from '../types';

import { ComposerBoxContext } from './composer-box';
import {
  boxAnchor,
  GAP,
  GUTTER,
  panelSide,
  viewportHeight,
  type PanelSide,
} from './popover-placement';

interface TokenMeterProps {
  /** The chat's newest context, with the model's window. */
  figure: MeterFigure;
  /** The session is busy: a turn runs, a stream is read, or a compaction runs. */
  busy: boolean;
  /** A compaction runs: this tab's press, or one a session read showed. */
  compacting: boolean;
  /** Compaction on Investbanq's own model is not available yet: the control is held. */
  off: boolean;
  /** No control: a connected session, or one compaction is not offered on. */
  withoutControl: boolean;
  /** Compact conversation was pressed. */
  onCompact: () => void;
}

/**
 * The composer's token meter (DEV-4598): a quiet 28 × 32 button before the Model chip whose ring
 * fills to how much of the model's input limit the chat's next message carries, and its Context
 * popover: the figures, a bar, what compacting does, and Compact conversation. The button is
 * never natively disabled: while it is unavailable it says why, and a press does nothing.
 */
export function TokenMeter({
  figure,
  busy,
  compacting,
  off,
  withoutControl,
  onCompact,
}: TokenMeterProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const locale = i18n.language;
  const box = useContext(ComposerBoxContext);
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<PanelSide>('bottom');
  const chipRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const compactRef = useRef<HTMLButtonElement>(null);
  const boxRef = box?.boxRef ?? chipRef;
  const anchor = useMemo(() => ({ current: boxAnchor(boxRef, chipRef) }), [boxRef]);
  const reasonId = `${useId()}-reason`;

  const { usedTokens, windowTokens, estimated } = figure;
  const figures = {
    used: tokenCount(usedTokens, locale),
    window: tokenCount(windowTokens, locale),
    percent: sharePercent(usedTokens, windowTokens, locale),
  };
  const name = estimated
    ? t(
        'composer.meterLabelEstimated',
        'Context: about {{used}} of {{window}} tokens ({{percent}})',
        figures,
      )
    : t('composer.meterLabel', 'Context: {{used}} of {{window}} tokens ({{percent}})', figures);
  const drawn = drawnShare(usedTokens, windowTokens);
  // Why Compact conversation cannot be pressed now, if it cannot.
  const reason = off
    ? t(
        'composer.noteCompactInternalOff',
        "Compaction on Investbanq's own model is not available yet.",
      )
    : compacting
      ? t('composer.compacting', 'Compacting…')
      : busy
        ? t('composer.compactBusy', 'Available when the current answer finishes.')
        : null;

  // A meter that goes while it holds focus (a press found the session gone) hands it to the
  // textarea. Its cleanup runs before its button leaves the page.
  const focused = useRef(false);
  useLayoutEffect(
    () => () => {
      if (focused.current) box?.focusInput();
    },
    [box],
  );

  /** The side the popover opens on, from its content's own height and the room by the box. */
  const decideSide = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;
    const r = anchor.current.getBoundingClientRect();
    const maxHeight = content.style.maxHeight;
    content.style.maxHeight = 'none';
    const height = content.offsetHeight;
    content.style.maxHeight = maxHeight;
    setSide(panelSide(height, r.top - GAP - GUTTER, viewportHeight() - r.bottom - GAP - GUTTER));
  }, [anchor]);

  // While open, a resize decides the side again.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', decideSide);
    return () => {
      window.removeEventListener('resize', decideSide);
    };
  }, [open, decideSide]);

  function handleCompact() {
    if (reason !== null) return;
    // The popover closes, focus goes back to the meter, and the composer's note follows it.
    setOpen(false);
    onCompact();
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={chipRef}
          type="button"
          aria-label={name}
          title={name}
          onFocus={() => {
            focused.current = true;
          }}
          onBlur={() => {
            focused.current = false;
          }}
          className="flex h-8 w-7 shrink-0 items-center justify-center rounded-md text-foreground outline-none transition-colors hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring data-[state=open]:bg-accent"
        >
          {/* A 16 px ring filled clockwise from 12 o'clock (counter-clockwise in RTL). */}
          <svg aria-hidden="true" viewBox="0 0 16 16" className="size-4 rtl:-scale-x-100">
            <circle
              cx="8"
              cy="8"
              r="6.5"
              fill="none"
              strokeWidth="2.5"
              className="stroke-muted-foreground/60"
            />
            {drawn > 0 && (
              <circle
                cx="8"
                cy="8"
                r="6.5"
                fill="none"
                strokeWidth="2.5"
                strokeLinecap="round"
                pathLength={100}
                strokeDasharray={`${String(drawn * 100)} 100`}
                transform="rotate(-90 8 8)"
                className="stroke-muted-foreground"
                data-share={drawn}
              />
            )}
          </svg>
        </button>
      </PopoverTrigger>
      <PopoverAnchor virtualRef={anchor} />
      <PopoverContent
        ref={contentRef}
        side={side}
        align="end"
        sideOffset={GAP}
        collisionPadding={GUTTER}
        updatePositionStrategy="always"
        aria-label={t('composer.contextTitle', 'Context')}
        tabIndex={-1}
        // Portalled outside the AI CIO panel, it is still its surface (see ModelPicker).
        data-ai-cio-surface
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          decideSide();
          (compactRef.current ?? contentRef.current)?.focus();
        }}
        onEscapeKeyDown={(e) => {
          // Escape closes the popover only, never the AI CIO panel.
          e.stopPropagation();
        }}
        className="max-h-[var(--radix-popover-content-available-height)] w-70 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg p-4 text-start"
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="shrink-0 text-sm font-medium text-foreground">
            {t('composer.contextTitle', 'Context')}
          </span>
          <span className="min-w-0 truncate text-xs text-muted-foreground">
            {estimated
              ? t('composer.contextFiguresEstimated', '~{{used}} / {{window}}', figures)
              : t('composer.contextFigures', '{{used}} / {{window}}', figures)}
          </span>
        </div>
        <div aria-hidden="true" className="mt-3 h-1 w-full rounded-[2px] bg-muted-foreground/60">
          <div
            className="h-full rounded-[2px] bg-muted-foreground"
            style={{ width: `${String(drawn * 100)}%` }}
          />
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          {t(
            'composer.contextBody',
            'The model rereads this conversation with every message. Compacting replaces older messages with a summary for the model; your chat stays as it is.',
          )}
        </p>
        {!withoutControl && (
          <>
            <Separator className="my-3" />
            <Button
              ref={compactRef}
              type="button"
              variant="secondary"
              size="sm"
              aria-disabled={reason !== null || undefined}
              aria-describedby={reason !== null ? reasonId : undefined}
              onClick={handleCompact}
              className="w-full aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            >
              <FoldVertical aria-hidden className="size-4" />
              {t('composer.compactButton', 'Compact conversation')}
            </Button>
            {reason !== null && (
              <p id={reasonId} className="mt-2 text-xs text-muted-foreground">
                {reason}
              </p>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
